import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

// ============================================================================
// Section Keys (Enum)
// ============================================================================

export enum ConfigSection {
  ModelPrompt = "model-prompt",
  BaseSettings = "iterations-temperature",
  Tools = "tools",
  Schema = "schema",
  Experimental = "experimental",
}

// ============================================================================
// Context
// ============================================================================

type AgentVersionConfigPanelContextProps = {
  // Accordion State
  accordionValues: string[];
  setAccordionValues: React.Dispatch<React.SetStateAction<string[]>>;
  /** Expand a section (no-op if already expanded) */
  expandSection: (section: ConfigSection) => void;

  // Scroll Management
  registerScrollFn: (section: ConfigSection, scrollFn: () => void) => void;
  scrollToSection: (section: ConfigSection) => void;

  // Schema Section State
  schemaTab: SchemaTab;
  setSchemaTab: React.Dispatch<React.SetStateAction<SchemaTab>>;
  outputMode: OutputMode;
  setOutputMode: React.Dispatch<React.SetStateAction<OutputMode>>;

  // JSON Schema Drawer State
  /** Tracks which schema is being edited in the JSON drawer with unsaved changes.
   * - false: Drawer is closed or open but not dirty
   * - "input": Editing input schema with unsaved changes
   * - "output": Editing output schema with unsaved changes
   */
  jsonSchemaDrawerEditing: SchemaTab | false;
  setJsonSchemaDrawerEditing: (state: SchemaTab | false) => void;

  // Focus Actions
  /** Focus a section - expands accordion and scrolls into view */
  focusSection: (section: ConfigSection) => void;
  /** Focus schema section with specific tab/mode - expands, sets state, and scrolls */
  focusSchemaSection: (options?: FocusSchemaSectionOptions) => void;
};

// Section-specific types (schema section)

export type SchemaTab = "input" | "output";
export type OutputMode = "structured" | "text";

type FocusSchemaSectionOptions = {
  tab?: SchemaTab;
  outputMode?: OutputMode;
  /** Delay before scrolling. Use longer delay when sidebar is closing. */
  scrollDelay?: number;
};

const AgentVersionConfigPanelContext =
  createContext<AgentVersionConfigPanelContextProps | null>(null);

// ============================================================================
// Provider
// ============================================================================

type AgentVersionConfigPanelProviderProps = {
  children: React.ReactNode;
  initialOutputMode?: OutputMode;
};

/**
 * Provides coordinated state management for the Agent Version configuration panel.
 *
 * Manages:
 * - Accordion state: which config sections are expanded/collapsed
 * - Scroll management: smooth scrolling to sections when expanded
 * - Focus actions: combined expand + scroll operations for navigating to sections
 *
 * Used by AgentVersionConfigurationForm and its child components to coordinate
 * UI state across the accordion-based configuration interface.
 */
export const AgentVersionConfigPanelProvider: React.FC<
  AgentVersionConfigPanelProviderProps
> = ({ children, initialOutputMode = "text" }) => {
  // Accordion state
  const [accordionValues, setAccordionValues] = useState<string[]>([
    ConfigSection.ModelPrompt,
  ]);

  // Scroll function registry
  const scrollFnsRef = useRef<Map<ConfigSection, () => void>>(new Map());

  // Schema section state
  const [schemaTab, setSchemaTab] = useState<SchemaTab>("input");
  const [outputMode, setOutputMode] = useState<OutputMode>(initialOutputMode);

  // JSON Schema Drawer editing state
  const [jsonSchemaDrawerEditing, setJsonSchemaDrawerEditing] = useState<
    SchemaTab | false
  >(false);

  const registerScrollFn = useCallback(
    // No clean-up function needed, because map size is bounded and
    // we overwrite on re-register
    (section: ConfigSection, scrollFn: () => void) => {
      scrollFnsRef.current.set(section, scrollFn);
    },
    [],
  );

  const scrollToSection = useCallback((section: ConfigSection) => {
    scrollFnsRef.current.get(section)?.();
  }, []);

  const expandSection = useCallback((section: ConfigSection) => {
    setAccordionValues((prev) =>
      prev.includes(section) ? prev : [...prev, section],
    );
  }, []);

  const focusSection = useCallback(
    (section: ConfigSection) => {
      expandSection(section);
      setTimeout(() => scrollToSection(section), 20);
    },
    [expandSection, scrollToSection],
  );

  // Schema section-specific focus action with optional tab/mode
  const focusSchemaSection = useCallback(
    (options?: FocusSchemaSectionOptions) => {
      expandSection(ConfigSection.Schema);
      if (options?.tab !== undefined) {
        setSchemaTab(options.tab);
      }
      if (options?.outputMode !== undefined) {
        setOutputMode(options.outputMode);
      }
      const delay = options?.scrollDelay ?? 20;
      setTimeout(() => scrollToSection(ConfigSection.Schema), delay);
    },
    [expandSection, scrollToSection],
  );

  return (
    <AgentVersionConfigPanelContext.Provider
      value={{
        accordionValues,
        setAccordionValues,
        expandSection,
        registerScrollFn,
        scrollToSection,
        schemaTab,
        setSchemaTab,
        outputMode,
        setOutputMode,
        jsonSchemaDrawerEditing,
        setJsonSchemaDrawerEditing,
        focusSection,
        focusSchemaSection,
      }}
    >
      {children}
    </AgentVersionConfigPanelContext.Provider>
  );
};

export const useAgentVersionConfigPanel = () => {
  const context = useContext(AgentVersionConfigPanelContext);
  if (!context) {
    throw new Error(
      "useAgentVersionConfigPanel must be used within ConfigurationFormProvider",
    );
  }
  return context;
};
